'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { Menu } from '../../components/menu';
import { Tooltip } from '../../components/tooltip';
import { ArrowDown01Icon, SquareIcon } from '@terra/ui-icons';
import { automationId } from '@terra/shared-utils';

export type SplitActionMenuItem = {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  disabledTitle?: string;
  onSelect: () => void;
};
export type SplitActionButtonProps = {
  primaryLabel: ReactNode;
  primaryAriaLabel: string;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  primaryTooltip?: string;
  primaryAutomationId?: string;
  onPrimaryClick: () => void;
  menuTriggerAriaLabel: string;
  menuTriggerDisabled?: boolean;
  menuItems: SplitActionMenuItem[];
  stopAction?: {
    label: string;
    onStop: () => void;
    loading?: boolean;
    disabled?: boolean;
  };
  groupTooltip?: string;
};

export function SplitActionButton({
  primaryLabel,
  primaryAriaLabel,
  primaryDisabled = false,
  primaryLoading = false,
  primaryTooltip,
  primaryAutomationId,
  onPrimaryClick,
  menuTriggerAriaLabel,
  menuTriggerDisabled = false,
  menuItems,
  stopAction,
  groupTooltip,
}: SplitActionButtonProps) {
  const groupRef = useRef<HTMLDivElement>(null);
  const [groupWidth, setGroupWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = groupRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => setGroupWidth(element.offsetWidth);

    updateWidth();
    const observer = new ResizeObserver(updateWidth);

    observer.observe(element);

    return () => observer.disconnect();
  }, []);
  if (stopAction) {
    return (
      <Button
        variant="outline"
        size="xs"
        colorPalette="red"
        disabled={stopAction.disabled}
        onClick={stopAction.onStop}
        loading={stopAction.loading}
      >
        <SquareIcon size={14} />
        {stopAction.label}
      </Button>
    );
  }

  const primaryButton = (
    <Button
      variant="outline"
      size="xs"
      onClick={onPrimaryClick}
      loading={primaryLoading}
      disabled={primaryDisabled}
      borderRightRadius="0"
      px={2.5}
      gap={2}
      aria-label={primaryAriaLabel}
      {...(primaryAutomationId ? automationId(primaryAutomationId) : {})}
    >
      {primaryLabel}
    </Button>
  );
  const isMenuTriggerDisabled =
    menuTriggerDisabled || primaryDisabled || primaryLoading;
  const splitButton = (
    <HStack gap={0} ref={groupRef}>
      {primaryTooltip && !primaryDisabled ? (
        <Tooltip title={primaryTooltip}>
          <Box as="span" display="inline-flex">
            {primaryButton}
          </Box>
        </Tooltip>
      ) : (
        primaryButton
      )}
      <Menu.Root
        positioning={{
          strategy: 'fixed',
          placement: 'bottom-end',
        }}
      >
        <Menu.Trigger asChild>
          <Button
            variant="outline"
            size="xs"
            aria-label={menuTriggerAriaLabel}
            disabled={isMenuTriggerDisabled}
            px={2}
            borderLeftRadius="0"
            borderLeftWidth="0"
          >
            <ArrowDown01Icon size={14} />
          </Button>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content
            minW={groupWidth ? `${groupWidth}px` : 'fit-content'}
            p="1"
            shadow="lg"
            borderWidth="0"
            bg="bg.canvas"
          >
            {menuItems.map((item) => (
              <Menu.Item
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                title={item.disabled ? item.disabledTitle : undefined}
                onClick={item.onSelect}
                display="flex"
                alignItems="center"
                gap="2"
              >
                {item.icon}
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </HStack>
  );

  if (groupTooltip) {
    return (
      <Tooltip title={groupTooltip}>
        <Box as="span" display="inline-flex">
          {splitButton}
        </Box>
      </Tooltip>
    );
  }

  return splitButton;
}
